import React, { useState } from 'react';
import CartItem from '../../components/CartItem';
import OrderSummaryModal from './OrderSummaryModal';
import SidePanel from '../../components/SidePanel';
import { useLanguage } from '../../contexts/LanguageContext';
import { CartIcon, ClockIcon, PenIcon, NoteIcon, TimesIcon } from '../../utils/iconMapping';
import soundManager from '../../utils/soundManager';

export default function Cart({ cart, onUpdateQty, onCheckout }) {
  const [isOpen, setIsOpen] = useState(false);
  const [showDeliveryPicker, setShowDeliveryPicker] = useState(false);
  const [showOrderSummary, setShowOrderSummary] = useState(false);
  const { t, language } = useLanguage();
  
  // Generate order number based on current date and time
  const getOrderNumber = () => {
    const now = new Date();
    const year = now.getFullYear();
    const month = String(now.getMonth() + 1).padStart(2, '0');
    const day = String(now.getDate()).padStart(2, '0');
    const hours = String(now.getHours()).padStart(2, '0');
    const minutes = String(now.getMinutes()).padStart(2, '0');
    const seconds = String(now.getSeconds()).padStart(2, '0');
    return `${year}${month}${day}${hours}${minutes}${seconds}`;
  };
  
  // Get next Monday
  const getNextMonday = () => {
    const today = new Date();
    const dayOfWeek = today.getDay();
    const daysUntilMonday = dayOfWeek === 0 ? 1 : (8 - dayOfWeek) % 7 || 7;
    const nextMonday = new Date(today);
    nextMonday.setDate(today.getDate() + daysUntilMonday);
    return nextMonday.toISOString().split('T')[0];
  };
  
  const [deliveryDate, setDeliveryDate] = useState(getNextMonday());
  const [deliveryTime, setDeliveryTime] = useState('19:30');
  
  // Format display text
  const formatDeliveryDisplay = () => {
    const date = new Date(deliveryDate + 'T' + deliveryTime);
    
    if (language === 'zh') {
      // Chinese format: 星期一, 19:30
      const daysZh = ['星期日', '星期一', '星期二', '星期三', '星期四', '星期五', '星期六'];
      const dayName = daysZh[date.getDay()];
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${dayName}, ${hours}:${minutes}`;
    } else {
      // English format: Monday, 7:30 PM
      const daysEn = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
      const dayName = daysEn[date.getDay()];
      const hours = date.getHours();
      const minutes = date.getMinutes();
      const period = hours >= 12 ? 'PM' : 'AM';
      const displayHours = hours % 12 || 12;
      const displayMinutes = minutes.toString().padStart(2, '0');
      return `${dayName}, ${displayHours}:${displayMinutes} ${period}`;
    }
  };
  
  // Handle order submission
  const handleSubmitOrder = async (markdown) => {
    const deliveryInfo = `${deliveryDate} at ${deliveryTime}`;
    await onCheckout(0, deliveryInfo, markdown, deliveryDate, deliveryTime);
    setIsOpen(false);
  };
  
  return (
    <>
      {/* Mobile Cart Button - Fixed at bottom */}
      <button
        onClick={() => {
          soundManager.playTap();
          setIsOpen(!isOpen);
        }}
        className="lg:hidden fixed bottom-4 right-4 bg-orange-500 text-white px-6 py-3 rounded-full shadow-lg flex items-center gap-2 z-30"
      >
        <CartIcon />
        {cart.length ? <span className="font-bold">({cart.length})</span> : ""}
      </button>

      {/* Cart Sidebar - Using SidePanel */}
      <SidePanel
        isOpen={isOpen}
        onClose={() => {
          soundManager.playTap();
          setIsOpen(false);
        }}
        title={
          <div>
            <h2 className="font-bold text-xl">{t('Current Order', '当前订单')}</h2>
            <p className="text-gray-400 text-sm">#{getOrderNumber()}</p>
          </div>
        }
        width="w-full sm:w-96"
        showBackdrop={true}
        closeOnClickOutside={false}
        closeOnEscape={true}
        footer={
          <div className="p-4 md:p-6 space-y-3">
            <button
              className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 md:py-4 rounded-xl font-bold text-base md:text-lg shadow-lg shadow-orange-200 transition-all"
              onClick={() => {
                soundManager.playTap();
                setShowOrderSummary(true);
              }}
            >
              {t('Get Summary', '获取摘要')}
            </button>
          </div>
        }
      >
        <div className="p-4 md:p-6 flex flex-col h-full">
          {/* Cart List */}
          <div className="flex-1 overflow-y-auto pr-2 space-y-4 md:space-y-6 mb-3">
          {cart.length === 0 ? (
            <div className="text-center text-gray-400 mt-10 flex flex-col items-center gap-2">
              <CartIcon size="2x" />
              <span>{t('Cart is empty', '购物车是空的')}</span>
            </div>
          ) : (
            cart.map(item => (
              <CartItem
                key={item.cartItemId || item.id}
                item={item}
                onUpdateQty={onUpdateQty}
              />
            ))
          )}
        </div>

        {/* Delivery Time Section */}
        <div className="mt-3 mb-3">
          <button
            onClick={() => {
              soundManager.playTap();
              setShowDeliveryPicker(true);
            }}
            className="w-full p-3 md:p-4 bg-white rounded-xl border-2 border-orange-200 hover:border-orange-300 hover:shadow-md transition-all flex items-center justify-between group"
          >
            <div className="flex items-center gap-2 md:gap-3">
              <div className="w-8 h-8 md:w-10 md:h-10 bg-orange-100 rounded-full flex items-center justify-center group-hover:bg-orange-200 transition-colors">
                <ClockIcon className="text-orange-500" />
              </div>
              <div className="text-left">
                <p className="text-[10px] md:text-xs text-gray-500 font-medium">
                  {t('Delivery Time', '送达时间')}
                </p>
                <p className="text-xs md:text-sm font-bold text-gray-800">{formatDeliveryDisplay()}</p>
              </div>
            </div>
            <PenIcon className="text-gray-400 group-hover:text-orange-500 transition-colors" />
          </button>
        </div>

        {/* Delivery Time Modal */}
        {showDeliveryPicker && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 bg-black bg-opacity-50 z-50"
              onClick={() => setShowDeliveryPicker(false)}
            />
            
            {/* Modal */}
            <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6 space-y-4">
                {/* Header */}
                <div className="flex items-center justify-between pb-4 border-b">
                  <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                    <ClockIcon className="text-orange-500" />
                    <span>{t('Select Delivery Time', '选择送达时间')}</span>
                  </h3>
                  <button
                    onClick={() => {
                      soundManager.playTap();
                      setShowDeliveryPicker(false);
                    }}
                    className="w-8 h-8 rounded-full hover:bg-gray-100 flex items-center justify-center text-gray-500 hover:text-gray-700 transition-colors"
                  >
                    <TimesIcon />
                  </button>
                </div>

                {/* Quick Select Buttons */}
                <div>
                  <p className="text-sm font-medium text-gray-600 mb-3">{t('Quick Select', '快速选择')}</p>
                  <div className="grid grid-cols-2 gap-2">
                    {[
                      { labelEn: 'Today 7:30 PM', labelZh: '今天 19:30', offset: 0, time: '19:30' },
                      { labelEn: 'Tomorrow 7:30 PM', labelZh: '明天 19:30', offset: 1, time: '19:30' },
                      { labelEn: 'Next Monday 7:30 PM', labelZh: '下周一 19:30', offset: null, time: '19:30' },
                      { labelEn: 'Next Week', labelZh: '下周', offset: 7, time: '19:30' }
                    ].map((option, idx) => (
                      <button
                        key={idx}
                        onClick={() => {
                          soundManager.playTap();
                          if (option.offset !== null) {
                            const date = new Date();
                            date.setDate(date.getDate() + option.offset);
                            setDeliveryDate(date.toISOString().split('T')[0]);
                            setDeliveryTime(option.time);
                          } else {
                            setDeliveryDate(getNextMonday());
                            setDeliveryTime(option.time);
                          }
                        }}
                        className="px-4 py-3 bg-orange-50 hover:bg-orange-100 border-2 border-orange-200 hover:border-orange-300 rounded-xl text-sm font-medium text-gray-700 transition-all hover:shadow-md"
                      >
                        {language === 'zh' ? option.labelZh : option.labelEn}
                      </button>
                    ))}
                  </div>
                </div>

                {/* Custom Date/Time Inputs */}
                <div className="space-y-3">
                  <p className="text-sm font-medium text-gray-600">{t('Custom Time', '自定义时间')}</p>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">{t('Date', '日期')}</label>
                    <input
                      type="date"
                      value={deliveryDate}
                      onChange={(e) => setDeliveryDate(e.target.value)}
                      min={new Date().toISOString().split('T')[0]}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300 bg-white text-sm"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-gray-600 mb-2">{t('Time', '时间')}</label>
                    <input
                      type="time"
                      value={deliveryTime}
                      onChange={(e) => setDeliveryTime(e.target.value)}
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:ring-orange-300 focus:border-orange-300 bg-white text-sm"
                    />
                  </div>
                </div>

                {/* Confirm Button */}
                <button
                  onClick={() => {
                    soundManager.playTap();
                    setShowDeliveryPicker(false);
                  }}
                  className="w-full bg-orange-500 hover:bg-orange-600 text-white py-3 rounded-xl font-semibold transition-colors shadow-lg hover:shadow-xl"
                >
                  {t('Confirm', '确认')}
                </button>
              </div>
            </div>
          </>
        )}

          {/* Special Instructions Section */}
          {cart.some(item => item.specialInstructions) && (
            <div className="mt-3 p-3 md:p-4 bg-orange-50 rounded-xl border border-orange-100">
              <h3 className="font-semibold text-xs md:text-base text-gray-800 mb-2 md:mb-3 flex items-center gap-1.5 md:gap-2">
                <NoteIcon className="text-orange-500" />
                  {t('Special Instructions', '特殊要求')}
              </h3>
              <div className="space-y-1.5 md:space-y-2">
                {cart.filter(item => item.specialInstructions).map((item, index) => (
                  <div key={`${item.id}-${index}`} className="text-xs md:text-sm">
                    <p className="font-medium text-gray-700">{item.name}:</p>
                    <p className="text-gray-600 italic pl-2">{item.specialInstructions}</p>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      </SidePanel>

      {/* Order Summary Modal */}
      <OrderSummaryModal
        isOpen={showOrderSummary}
        onClose={() => setShowOrderSummary(false)}
        cart={cart}
        deliveryDate={deliveryDate}
        deliveryTime={deliveryTime}
        onSubmit={handleSubmitOrder}
      />
    </>
  );
}


